import { parseISO } from 'date-fns';
import type {
  ArTaskRow,
  CommentResponse,
  InvoiceEvent,
  TaskTimelineEntry,
} from '@erp/rpc';

/** The ERP's event type for a note on the invoice (Famous comments/notes). */
const ERP_NOTE_EVENT_TYPE = 'Note Added';

/** Task timeline kinds the feed shows; the rest stay on the task page. */
const FEED_TIMELINE_KINDS: ReadonlySet<TaskTimelineEntry['kind']> = new Set([
  'task_created',
  'task_status_changed',
  'task_assignee_changed',
  // A closed task's outcome and note, for tasks closed before the audit
  // trail (later ones carry them on their status change).
  'task_concluded',
]);

/** A sales order note, as its payload carries it. */
interface SalesOrderNote {
  text: string;
  createdAt: Date;
  userId: string;
}

export type ArTaskFeedEntry =
  /** The task's own note: editable by its author. */
  | { kind: 'comment'; id: string; at: Date; comment: CommentResponse }
  /** A note the ERP owns (invoice) or the order carries: read-only. */
  | {
      kind: 'erp_note';
      id: string;
      at: Date;
      body: string;
      source: 'invoice' | 'sales_order';
      /** The feed sent it without a date; it sorts first, with no date shown. */
      undated?: boolean;
      /** How many times it repeated back to back; only the newest shows. */
      repeats?: number;
    }
  /** Any other invoice event (sent, opened, payment…): one line. */
  | {
      kind: 'record_event';
      id: string;
      at: Date;
      event: InvoiceEvent;
      /** How many times it repeated back to back; only the newest shows. */
      repeats?: number;
    }
  | { kind: 'task_event'; id: string; at: Date; entry: TaskTimelineEntry };

export type ArTaskFeedFilter = 'all' | 'notes' | 'erp';

export interface ArTaskFeedSources {
  comments: readonly CommentResponse[];
  invoiceEvents: readonly InvoiceEvent[];
  salesOrderNotes: readonly SalesOrderNote[];
  /** The row's copy of the invoice's newest ERP note (the feed's). */
  rowNote: ArTaskRow['invoiceNote'] | undefined;
  timeline: readonly TaskTimelineEntry[];
}

/** The latest date JavaScript can hold, to sort an undated note first. */
const LATEST = new Date(8.64e15);

/** Notes compare by text alone: the feed and Gently's copy differ in time. */
function noteKey(body: string): string {
  return body.replace(/\s+/g, ' ').trim().toLowerCase();
}

function invoiceEventEntry(event: InvoiceEvent): ArTaskFeedEntry {
  const at = new Date(event.occurredAt);
  if (event.eventType === ERP_NOTE_EVENT_TYPE && event.note) {
    const { id, note } = event;
    return { kind: 'erp_note', id, at, body: note, source: 'invoice' };
  }
  return { kind: 'record_event', id: event.id, at, event };
}

/**
 * The row's ERP note, unless Gently already has it as a "Note Added" event:
 * the feed can be newer than the invoice's synced events.
 */
function rowNoteEntry(
  rowNote: ArTaskFeedSources['rowNote'],
  invoiceEvents: readonly InvoiceEvent[],
): ArTaskFeedEntry[] {
  if (!rowNote?.body.trim()) return [];
  const key = noteKey(rowNote.body);
  const synced = invoiceEvents.some(
    (event) =>
      event.eventType === ERP_NOTE_EVENT_TYPE &&
      noteKey(event.note ?? '') === key,
  );
  if (synced) return [];
  // A date-only note sorts at that day's start; an undated one is the
  // feed's newest, so it goes first.
  const at = rowNote.date ? parseISO(rowNote.date) : LATEST;
  return [
    {
      kind: 'erp_note',
      id: 'row-note',
      at,
      body: rowNote.body,
      source: 'invoice',
      undated: !rowNote.date,
    },
  ];
}

/** What makes two ERP notes or record events the same; null for the rest. */
function repeatKey(entry: ArTaskFeedEntry): string | null {
  if (entry.kind === 'erp_note') {
    return `note:${entry.source}:${noteKey(entry.body)}`;
  }
  if (entry.kind === 'record_event') {
    return `event:${entry.event.eventType}:${noteKey(entry.event.note ?? '')}`;
  }
  return null;
}

type RepeatableEntry = Extract<
  ArTaskFeedEntry,
  { kind: 'erp_note' | 'record_event' }
>;

function isRepeatable(
  entry: ArTaskFeedEntry | undefined,
): entry is RepeatableEntry {
  return entry?.kind === 'erp_note' || entry?.kind === 'record_event';
}

/**
 * Keeps only the newest of back-to-back repeats of the same ERP note or
 * record event (e.g. a weekly "[AR] Sent Recon sheet" note), counting them.
 * The task's own notes and events never collapse.
 */
function collapseRepeats(newestFirst: ArTaskFeedEntry[]): ArTaskFeedEntry[] {
  const collapsed: ArTaskFeedEntry[] = [];
  let previousKey: string | null = null;
  for (const entry of newestFirst) {
    const key = repeatKey(entry);
    const previous = collapsed.at(-1);
    if (key !== null && key === previousKey && isRepeatable(previous)) {
      previous.repeats = (previous.repeats ?? 1) + 1;
      continue;
    }
    // A copy, so counting repeats never touches the caller's entries.
    collapsed.push({ ...entry });
    previousKey = key;
  }
  return collapsed;
}

/**
 * One newest-first feed for a task: its notes, the invoice's ERP notes and
 * events (or the order's notes), and the task's own key events. Back-to-back
 * repeats of an ERP note or event show once, as the newest.
 */
export function buildArTaskFeed(sources: ArTaskFeedSources): ArTaskFeedEntry[] {
  const entries: ArTaskFeedEntry[] = [
    ...sources.comments.map((comment): ArTaskFeedEntry => ({
      kind: 'comment',
      id: comment.id,
      at: new Date(comment.createdAt),
      comment,
    })),
    ...sources.invoiceEvents.map(invoiceEventEntry),
    ...rowNoteEntry(sources.rowNote, sources.invoiceEvents),
    ...sources.salesOrderNotes.map((note, index): ArTaskFeedEntry => ({
      kind: 'erp_note',
      id: `so-note-${index}`,
      at: new Date(note.createdAt),
      body: note.text,
      source: 'sales_order',
    })),
    ...sources.timeline.flatMap((entry): ArTaskFeedEntry[] =>
      FEED_TIMELINE_KINDS.has(entry.kind)
        ? [
            {
              kind: 'task_event',
              id: entry.id,
              at: new Date(entry.timestamp),
              entry,
            },
          ]
        : [],
    ),
  ];
  return collapseRepeats(
    entries.sort((a, b) => b.at.getTime() - a.at.getTime()),
  );
}

/** Notes: the task's own; ERP: the record's read-only notes. */
export function filterArTaskFeed(
  entries: readonly ArTaskFeedEntry[],
  filter: ArTaskFeedFilter,
): ArTaskFeedEntry[] {
  if (filter === 'notes') {
    return entries.filter((entry) => entry.kind === 'comment');
  }
  if (filter === 'erp') {
    return entries.filter((entry) => entry.kind === 'erp_note');
  }
  return [...entries];
}

/**
 * The record's side of the feed: the invoice's events for a not-sent task,
 * the sales order's notes for a shipped one.
 */
export function arTaskRecordFeedSources(
  kind: ArTaskRow['kind'] | undefined,
  records: {
    invoice: { invoiceEvents: InvoiceEvent[] } | null;
    salesOrder: { notes?: SalesOrderNote[] | null } | null;
  },
): Pick<ArTaskFeedSources, 'invoiceEvents' | 'salesOrderNotes'> {
  if (kind === 'to_invoice') {
    return {
      invoiceEvents: [],
      salesOrderNotes: records.salesOrder?.notes ?? [],
    };
  }
  return {
    invoiceEvents: records.invoice?.invoiceEvents ?? [],
    salesOrderNotes: [],
  };
}
